/**
 * Three responsibilities:
 *   1. Snap any .quantity__input whose value is below its data-min up to the
 *      data-min on init / cart updates / variant changes (visual fix).
 *   2. Auto-correct the actual cart server-side: if any cart line item's
 *      quantity is below the product's effective minimum (Min5-tagged
 *      product → min 5), POST /cart/update.js to bump it. This is the only
 *      way to fix stale carts that were below the minimum before this
 *      enforcement shipped.
 *   3. Block submit on any /cart-bound form when any quantity input is below
 *      data-min. Final-line guard.
 *
 * The cart auto-correct uses /cart/update.js with the line index (1-based).
 * It runs ONCE per session (sessionStorage flag) to avoid loops.
 *
 * Loaded on cart drawer, cart page, and product page.
 */
(function () {
  const SESSION_FLAG = 'gbgtex_min5_corrected';

  function snapInputs(root) {
    const inputs = (root || document).querySelectorAll('.quantity__input');
    inputs.forEach(function (input) {
      const min = parseInt(input.dataset.min, 10);
      if (!min || min <= 1) return;
      const value = parseInt(input.value, 10);
      if (Number.isNaN(value) || value < min) {
        input.value = min;
        if (input.getAttribute('min') === null || parseInt(input.getAttribute('min'), 10) < min) {
          input.setAttribute('min', String(min));
        }
      }
    });
  }

  /**
   * Read the rendered DOM for cart line items: any .quantity__input with
   * data-min > 1 + value < data-min represents a Min5 product that's
   * undersized. POST /cart/update.js to bump those lines server-side. Cart
   * drawer + cart page will re-render from the corrected cart on success.
   *
   * Runs once per session via sessionStorage flag — prevents loops if the
   * server somehow doesn't accept the update.
   */
  function autoCorrectCart() {
    if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(SESSION_FLAG) === '1') {
      return Promise.resolve();
    }
    const inputs = document.querySelectorAll('.quantity__input[data-quantity-variant-id]');
    const updates = {};
    let needsUpdate = false;
    for (const input of inputs) {
      const variantId = input.dataset.quantityVariantId;
      const min = parseInt(input.dataset.min, 10);
      const value = parseInt(input.value, 10);
      if (!variantId || !min || min <= 1) continue;
      if (Number.isNaN(value) || value < min) {
        updates[variantId] = min;
        needsUpdate = true;
      }
    }
    if (!needsUpdate) {
      // Mark only after a confirmed empty pass — we want to keep retrying
      // on subsequent loads if something changes.
      return Promise.resolve();
    }
    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates }),
    })
      .then(function (r) { return r.json(); })
      .then(function () {
        if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(SESSION_FLAG, '1');
        document.dispatchEvent(new CustomEvent('cart:refreshed'));
        document.dispatchEvent(new CustomEvent('cart:updated'));
        // Replace cart-drawer markup with a freshly fetched render so the
        // user sees corrected quantities immediately.
        return fetch('/?section_id=cart-drawer', { credentials: 'same-origin' })
          .then(function (r) { return r.text(); })
          .then(function (html) {
            const parsed = new DOMParser().parseFromString(html, 'text/html');
            const fresh = parsed.querySelector('cart-drawer');
            const current = document.querySelector('cart-drawer');
            if (fresh && current) current.replaceWith(fresh);
            snapInputs(document);
          })
          .catch(function () { /* non-fatal */ });
      })
      .catch(function () { /* non-fatal */ });
  }

  function checkCart(form) {
    const inputs = form.querySelectorAll('.quantity__input');
    for (const input of inputs) {
      const min = parseInt(input.dataset.min, 10);
      if (!min || min <= 1) continue;
      const value = parseInt(input.value, 10);
      if (Number.isNaN(value) || value < min) {
        return { input, min, value: Number.isNaN(value) ? 0 : value };
      }
    }
    return null;
  }

  function attach(form) {
    if (!form || form.dataset.minQtyGuardAttached === '1') return;
    form.dataset.minQtyGuardAttached = '1';
    snapInputs(form);
    form.addEventListener(
      'submit',
      function (event) {
        const violation = checkCart(form);
        if (!violation) return;
        event.preventDefault();
        event.stopPropagation();
        const msg = `Beställningsminimum är ${violation.min} dm för detta tyg. Justera kvantiteten innan du går vidare.`;
        violation.input.focus();
        violation.input.select && violation.input.select();
        if (typeof violation.input.setCustomValidity === 'function') {
          violation.input.setCustomValidity(msg);
          violation.input.reportValidity();
        } else {
          alert(msg);
        }
      },
      true
    );
  }

  function init() {
    snapInputs(document);
    document.querySelectorAll('form[action*="/cart"]').forEach(attach);
    autoCorrectCart();
  }

  function initWithLateRetry() {
    init();
    // Re-snap after other scripts (product-info.js setQuantityBoundries, cart sync, etc.)
    // have settled on the input value. Two passes covers connected callbacks
    // and async cart fetches.
    setTimeout(init, 50);
    setTimeout(init, 250);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWithLateRetry);
  } else {
    initWithLateRetry();
  }
  document.addEventListener('cart:refreshed', init);
  document.addEventListener('cart:updated', init);
  // Re-snap after any AJAX cart drawer / cart page replacement.
  const mo = new MutationObserver(init);
  mo.observe(document.documentElement, { childList: true, subtree: true });
})();
